import Vue from 'vue'
import Vuex from 'vuex'
import { Command } from '@/Command'
import User from '@/User'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat' // import plugin
import 'dayjs/locale/zh-cn' // import locale
import * as _ from 'lodash'
import CloudGpxInfo from './CloudGpxInfo'
import SubscriptionValidResult from './SubscriptionValidResult'

dayjs.extend(localizedFormat) // use plugin

Vue.use(Vuex)

const claimsToBeSubscribed = function(state: any) {
    return state.user !== null && state.user.isSubscribed === true
}
const isSubscribed = function(state: any) {
    const res = claimsToBeSubscribed(state) && 
        !(state.subscriptionValidResult !== null && state.subscriptionValidResult.isValid === false);
    return res;
}

const store = new Vuex.Store({
    state: {
        loggedInUser: null,
        userCommands: [] as Command[],
        user: null as User | null,
        subscriptionExpiry:  null as Date|null,
        loginAttempted: false,
        currentTrackValue: '',
        showSubscriptionInvalid: false,
        subscriptionValidResult: null as SubscriptionValidResult|null,
        showingPricingSidebar: false
    },
    getters: {
        showingPricingSidebar: state => {
            return state.showingPricingSidebar;
        },
        userCommands: state => {
            return state.userCommands
        },
        claimsToBeSubscribed: state => {
            return claimsToBeSubscribed(state);
        },
        isSubscribed: state => {
            return isSubscribed(state)
        },
        isOrMightBeSubscribed: state => {
            return (!state.loginAttempted) || isSubscribed(state) // if they're currently logging in (loginAttempted = false), then they might still be a subscriber
        },
        isCustomer: state => {
            return state.user !== null && state.user.isCustomer
        },
        isSubscriber: state => {
            return state.user !== null && state.user.isSubscriber;
        },  
        isExpired: state => {
            return state.subscriptionExpiry !== null && state.subscriptionExpiry < new Date()
        },
        expiryDateString: state => {
            return state.subscriptionExpiry === null ? "" : dayjs(state.subscriptionExpiry).format('ddd, ll')
        },
        expiryDateStringWithTime: state => {
            return state.subscriptionExpiry === null ? "" : dayjs(state.subscriptionExpiry).format('ddd, ll,  h:mm a')
        },
        hasUserAlreadyUsedUpFreeTrial: state => {
            return state.user === null ? false : state.user.hasUserAlreadyUsedUpFreeTrial
        },
        loggedInUser: state => { return state.user },
        isUserLoggedIn: state => { return state.user !== null },
        wantsToUpgrade: state => { return state.user !== null && state.user.wantsToUpgrade},
        loggedInUserEmail: state => { return state.user?.email },
        hasPrivateGpxs: state => { return state.user && state.user.privateGpxs && state.user.privateGpxs.length > 0},
        hasPublicGpxs: state => { return state.user && state.user.publicGpxs && state.user.publicGpxs.length > 0},
        loginAttempted: state => { return state.loginAttempted },
        currentTrackValue: state => { return state.currentTrackValue },
        showSubscriptionInvalid: state => { return state.showSubscriptionInvalid },
        subscriptionValidManageSubscriptionUrl: state => { return state.subscriptionValidResult !== null ? state.subscriptionValidResult.manageSubscriptionUrl : null; }
    },
    mutations: {
        showingPricingSidebar(state, showing) {
                        state.showingPricingSidebar = showing
                    },
        pushCommand(state, msg: Command) {
            state.userCommands.push(msg)
        },
        handledCommand(state, id: string) {
            const index = state.userCommands.findIndex((m) => m.id === id)
            if (index >= 0) {
                state.userCommands.splice(index, 1)
            }
        },
        login(state, user: User) {
            state.user = user
            if(user.subscriptionExpiry) {
                state.subscriptionExpiry = new Date(user.subscriptionExpiry)
            } else {
                state.subscriptionExpiry = null;
            }
            if (user.wantsToUpgrade) {
                state.showingPricingSidebar = true;
            }
        },
        loginAttempted(state) {
            state.loginAttempted = true
        },
        logout(state) {
            state.user = null
            state.subscriptionExpiry = null
        },
        setSubscriptionExpiry(state, date: Date) {
            state.subscriptionExpiry = date
        },
        setSubscriptionInvalid(state, result: SubscriptionValidResult) {
            state.subscriptionValidResult = result;
            state.showSubscriptionInvalid = true
        },
        dismissSubscriptionInvalid(state) {
            state.showSubscriptionInvalid = false; //just dismisses the dialog
            // don't set subscriptionValidResult to null, otherwise they will gain access...
        },
        deletePublicGpx(state, gpxUrl: string) {
            if(state.user?.publicGpxs) {
                const index = state.user.publicGpxs.findIndex(m => m.url === gpxUrl)
                if(index >= 0) {
                    state.user.publicGpxs.splice(index, 1)
                }
            }
        },
        deletePrivateGpx(state, gpxUrl: string) {
            if(state.user?.privateGpxs) {
                const index = state.user.privateGpxs.findIndex(m => m.url === gpxUrl)

                if(index >= 0) {
                    state.user.privateGpxs.splice(index, 1)
                }
            }
        },
        savePublicGpx(state, newGpx: CloudGpxInfo) {
            if(state.user) {
                if(!state.user.publicGpxs) {
                    state.user.publicGpxs = []
                }
                const existing = _.find(state.user.publicGpxs, s => s.url === newGpx.url)
                if(existing) {
                    existing.modified = newGpx.modified
                }
                else {
                    state.user.publicGpxs.push(newGpx)
                    _.sortBy(state.user.publicGpxs)
                }
            }
        },
        savePrivateGpx(state, newGpx: CloudGpxInfo) {
            if(state.user) {
                if(!state.user.privateGpxs) {
                    state.user.privateGpxs = []
                }
                const existing = _.find(state.user.privateGpxs, s => s.url === newGpx.url)
                if(existing) {
                    existing.modified = newGpx.modified
                }
                else {
                    state.user.privateGpxs.push(newGpx)
                    _.sortBy(state.user.privateGpxs)
                }
            }
        },
        setCurrentTrackValue(state, id: string) {
            state.currentTrackValue = id
        }
    }
})

export default store